import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { EmployeeList } from '@/features/hr-employees/employee-list'
import type { EmployeeListParams } from '@/features/hr-employees/types'
import { meQuery } from '@/lib/me'
import { canSeeWith } from '@/lib/rail-visibility'

/**
 * Route shell for the employees list.
 *
 * Route definition, guard, render — nothing else, per the route-shell rule in
 * `apps/company/AGENTS.md`. `validateSearch` belongs here because the URL is a
 * route concern; what the screen does with the values is the feature's.
 *
 * Filter, sort and page live in the search params rather than in a store, so a
 * filtered employee list is linkable and survives a reload — which is exactly
 * the kind of view one person sends another.
 *
 * The grant guard is presentation only. The API enforces `employee.view` on
 * every endpoint, so a stale grant still 403s server-side; this keeps a user
 * without it out of a screen they cannot use.
 */
export const Route = createFileRoute('/_authenticated/hr/employees/')({
  validateSearch: (search: Record<string, unknown>): EmployeeListParams => ({
    search: typeof search.search === 'string' ? search.search : undefined,
    status: toStringArray(search.status),
    department_id: toStringArray(search.department_id),
    branch_id: toStringArray(search.branch_id),
    position_id: toStringArray(search.position_id),
    manager_employee_id: toStringArray(search.manager_employee_id),
    employment_type: toStringArray(search.employment_type),
    sort: typeof search.sort === 'string' ? search.sort : undefined,
    direction: search.direction === 'desc' ? 'desc' : undefined,
    page: typeof search.page === 'number' && search.page > 1 ? search.page : undefined,
    per_page:
      typeof search.per_page === 'number' &&
      search.per_page >= 1 &&
      search.per_page <= 100 &&
      search.per_page !== 30
        ? search.per_page
        : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (
      !canSeeWith(context.queryClient.getQueryData(meQuery.queryKey), {
        module: 'hr',
        permission: 'employee.view',
        minScope: 'department',
      })
    ) {
      throw redirect({ to: '/' })
    }
  },
  component: EmployeesRoute,
})

function EmployeesRoute() {
  const navigate = useNavigate()
  const params = Route.useSearch()

  return (
    <EmployeeList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/hr/employees', search: next })}
    />
  )
}

/**
 * A repeated query parameter arrives as an array; a single one as a string.
 * Both mean the same thing to the backend's `whereIn`.
 */
function toStringArray(value: unknown): string[] | undefined {
  if (typeof value === 'string') return [value]
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string')
  return undefined
}
